import { getJson, getList, sendJson } from '@/lib/api';
import type { PaginationMeta } from '@/lib/api';

/**
 * Client types + fetchers for the expenses explorer and the P&L. Mirrors the
 * server contract (api/v3/finance/*): the mirrored QuickBooks expense lines,
 * the spend rollups, and the monthly profit/loss. Amounts are integers in
 * dollars; `amount` is positive for money out (spend), negative for a credit.
 */

export const EXPENSES_PAGE_SIZE = 50;

/** Where the Analytics → Accounting pages live. */
export const ACCOUNTING_PATH = {
  cashFlow: '/analytics/accounting/cash-flow',
  receivables: '/analytics/accounting/receivables',
  assumptions: '/analytics/accounting/assumptions',
  forecastAccuracy: '/analytics/accounting/forecast-accuracy',
  expenses: '/analytics/accounting/expenses',
  pnl: '/analytics/accounting/pnl',
} as const;

/** Pre-Accounting URLs, kept as redirects so bookmarks and old links still land. */
export const LEGACY_ACCOUNTING_PATH = {
  expenses: '/expenses',
  pnl: '/pnl',
} as const;

/** The P&L buckets, in display order — must match the server's PNL_CATEGORIES. */
export const PNL_CATEGORIES = [
  { key: 'salaries', label: 'Salaries' },
  { key: 'contractor', label: 'Contractor expenses' },
  { key: 'travelMeals', label: 'Travel & meals' },
  { key: 'supplies', label: 'Supplies' },
  { key: 'interest', label: 'Interest' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'advertising', label: 'Advertising' },
  { key: 'shipping', label: 'Shipping' },
  { key: 'misc', label: 'Misc' },
] as const;
export type PnlCategory = (typeof PNL_CATEGORIES)[number]['key'];

export function pnlCategoryLabel(key: string): string {
  return PNL_CATEGORIES.find((c) => c.key === key)?.label ?? 'Misc';
}

export interface FinanceTransaction {
  readonly transactionLineId: string;
  readonly transactionId: string;
  readonly transactionType: string;
  readonly transactionDate: string;
  readonly amount: number;
  readonly vendor: string;
  readonly vendorId: string;
  readonly category: string;
  readonly categoryId: string;
  readonly memo: string;
  readonly isSpend: boolean;
  readonly pnlCategory: string;
  readonly pnlCategorySource: string;
}

export interface SpendGroup {
  readonly key: string;
  readonly amount: number;
}

export interface FinanceSummary {
  readonly window: { readonly from: string; readonly to: string };
  readonly spend: number;
  readonly byMonth: readonly SpendGroup[];
}

/** Full category + vendor spend lists (with amounts) for the window. */
export interface FinanceFacets {
  readonly categories: readonly SpendGroup[];
  readonly vendors: readonly SpendGroup[];
}

/** The sortable columns of the expenses table (keys the server maps to fields). */
export const EXPENSE_SORT_KEYS = ['date', 'amount', 'vendor', 'category'] as const;
export type ExpenseSortKey = (typeof EXPENSE_SORT_KEYS)[number];

export interface FinanceWindow {
  readonly from: string;
  readonly to: string;
}

export interface FinanceListParams extends FinanceWindow {
  readonly search?: string;
  readonly category?: string;
  readonly vendor?: string;
  readonly pnl?: string;
  readonly page: number;
  readonly sort?: { readonly key: string; readonly direction: 'asc' | 'desc' };
}

/** A page of expense lines for the explorer table. */
export async function listFinanceTransactions(
  params: FinanceListParams,
  signal?: AbortSignal,
): Promise<{ items: FinanceTransaction[]; pagination: PaginationMeta }> {
  return getList<FinanceTransaction>(
    '/finance/transactions',
    {
      from: params.from,
      to: params.to,
      page: params.page,
      limit: EXPENSES_PAGE_SIZE,
      ...(params.search ? { search: params.search } : {}),
      ...(params.category ? { category: params.category } : {}),
      ...(params.vendor ? { vendor: params.vendor } : {}),
      ...(params.pnl ? { pnl: params.pnl } : {}),
      ...(params.sort ? { sort: params.sort.key, dir: params.sort.direction } : {}),
    },
    signal,
  );
}

/** Sets one transaction's P&L bucket by hand (and learns the vendor rule server-side). */
export async function setTransactionCategory(
  transactionLineId: string,
  pnlCategory: PnlCategory,
): Promise<{ transaction: FinanceTransaction; vendorLinesUpdated: number }> {
  return sendJson('patch', `/finance/transactions/${encodeURIComponent(transactionLineId)}`, { pnlCategory });
}

/** Spend total + by-month for a window (drill-down when scoped by any filter). */
export async function readFinanceSummary(
  params: FinanceWindow & { readonly category?: string; readonly vendor?: string; readonly pnl?: string },
  signal?: AbortSignal,
): Promise<FinanceSummary> {
  return getJson<FinanceSummary>(
    '/finance/summary',
    {
      from: params.from,
      to: params.to,
      ...(params.category ? { category: params.category } : {}),
      ...(params.vendor ? { vendor: params.vendor } : {}),
      ...(params.pnl ? { pnl: params.pnl } : {}),
    },
    signal,
  );
}

/** The category + vendor filter options (by spend) over a window. */
export async function readFinanceFacets(params: FinanceWindow, signal?: AbortSignal): Promise<FinanceFacets> {
  return getJson<FinanceFacets>('/finance/facets', { from: params.from, to: params.to }, signal);
}

// ─── P&L ─────────────────────────────────────────────────────────────────────

/** Which date revenue is recognized on — matches the server's REVENUE_BASIS. */
export const REVENUE_BASIS = {
  event: 'event',
  signed: 'signed',
} as const;
export type RevenueBasis = (typeof REVENUE_BASIS)[keyof typeof REVENUE_BASIS];

export interface PnlMonth {
  readonly month: string;
  readonly revenue: number;
  readonly expenses: Readonly<Record<string, number>>;
  readonly totalExpenses: number;
  readonly net: number;
}

export interface PnlReport {
  readonly months: readonly PnlMonth[];
  readonly categories: ReadonlyArray<{ readonly key: string; readonly label: string }>;
  readonly totals: {
    readonly revenue: number;
    readonly expenses: Readonly<Record<string, number>>;
    readonly totalExpenses: number;
    readonly net: number;
  };
}

/** The monthly profit/loss: signed-event revenue (by event or signed date) vs. bucketed expenses. */
export async function readPnl(
  params: FinanceWindow & { readonly basis?: RevenueBasis },
  signal?: AbortSignal,
): Promise<PnlReport> {
  return getJson<PnlReport>(
    '/finance/pnl',
    { from: params.from, to: params.to, ...(params.basis ? { basis: params.basis } : {}) },
    signal,
  );
}
